import { AppUser } from '../types';

const API_BASE = '/api/property-comments';
const CACHE_TTL_MS = 60_000; // 1 minute cache

// In-memory cache for property comment counts: code -> { count, timestamp }
const countsCache = new Map<string, { count: number; timestamp: number }>();
// In-flight request deduplication: cacheKey -> Promise<Record<string, number>>
const inFlightRequests = new Map<string, Promise<Record<string, number>>>();

const getHeaders = (appUser: AppUser | null): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (appUser?.id) headers['x-user-id'] = appUser.id;
  if (appUser?.role) headers['x-user-role'] = appUser.role;
  return headers;
};

export const updateLocalCommentCountCache = (propertyCode: string, delta: number) => {
  const cached = countsCache.get(propertyCode);
  const current = cached ? cached.count : 0;
  const next = Math.max(0, current + delta);
  countsCache.set(propertyCode, { count: next, timestamp: Date.now() });
};

export const fetchPropertyCommentCounts = async (
  propertyCodes: string[],
  appUser: AppUser | null,
  forceRefresh = false
): Promise<Record<string, number>> => {
  if (!propertyCodes || propertyCodes.length === 0) return {};

  const cleanCodes = Array.from(new Set(propertyCodes.filter(Boolean).map(String)));
  if (cleanCodes.length === 0) return {};

  const now = Date.now();
  const result: Record<string, number> = {};
  const codesToFetch: string[] = [];

  if (!forceRefresh) {
    for (const code of cleanCodes) {
      const cached = countsCache.get(code);
      if (cached && now - cached.timestamp < CACHE_TTL_MS) {
        result[code] = cached.count;
      } else {
        codesToFetch.push(code);
      }
    }
  } else {
    codesToFetch.push(...cleanCodes);
  }

  // If all codes were found in cache, return immediately without network call
  if (codesToFetch.length === 0) {
    return result;
  }

  // Deduplicate concurrent in-flight requests for the same set of codes
  const sortedKey = [...codesToFetch].sort().join(',');
  const existingFlight = inFlightRequests.get(sortedKey);
  if (existingFlight) {
    const fetched = await existingFlight;
    return { ...result, ...fetched };
  }

  const fetchPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/batch-summary`, {
        method: 'POST',
        headers: getHeaders(appUser),
        body: JSON.stringify({ propertyCodes: codesToFetch }),
      });

      if (!res.ok) return {};
      const data = await res.json();
      const serverCounts: Record<string, number> = data.counts || {};

      // Store fetched results in cache (default to 0 for queried codes with no comments)
      for (const code of codesToFetch) {
        const count = serverCounts[code] || 0;
        countsCache.set(code, { count, timestamp: Date.now() });
      }

      return serverCounts;
    } catch (err) {
      console.warn('[propertyCommentsSummaryService] Failed to load comment counts:', err);
      return {};
    } finally {
      inFlightRequests.delete(sortedKey);
    }
  })();

  inFlightRequests.set(sortedKey, fetchPromise);
  const fetched = await fetchPromise;

  return { ...result, ...fetched };
};
